<script lang="ts" setup>
import {onMounted, onUnmounted} from "vue";
import IconBtnClose from "@/components/buttons/IconBtnClose.vue";

type ModalSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl';

const props = withDefaults(defineProps<{
  show: boolean,
  showClose?: boolean,
  showFooter?: boolean,
  size?: ModalSize,
  closeLabel?: string,
}>(), {
  showClose: true,
  showFooter: true,
  size: 'sm',
  closeLabel: 'Close',
});

const emit = defineEmits(['close']);

const close = () => {
  emit('close');
};

const handleKeydown = (e: KeyboardEvent) => {
  if (props.show && e.key === 'Escape') {
    close();
  }
};

onMounted(() => {
  document.addEventListener('keydown', handleKeydown);
});

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeydown);
});
</script>

<template>
  <Teleport to="body">
    <div v-if="show" class="modal-overlay" @click.self="close">
      <div :class="['modal-container', `modal-${size}`]">
        <header class="modal-header">
          <slot name="header">
            <h2>Modal Title</h2>
          </slot>
          <div v-if="showClose" class="close-div" @click="close">
            <span v-if="closeLabel" class="close-label-div">{{ closeLabel }}</span>
            <IconBtnClose aria-label="Close" class="close-btn"/>
          </div>
        </header>
        <main class="modal-body">
          <slot></slot>
        </main>
        <footer v-if="showFooter" class="modal-footer">
          <slot name="footer">
            <button class="btn" @click="close">Close</button>
          </slot>
        </footer>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.modal-container {
  background: #f4f5f9;
  border-radius: 4px;
  max-height: calc(100vh - 60px);
  max-width: 600px;
  width: calc(100vw - 60px);
  overflow: hidden;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  transition: all 0.3s;
}

.modal-xs { max-width: 400px; }
.modal-sm { max-width: 600px; }
.modal-md { max-width: 800px; }
.modal-lg { max-width: 1000px; }
.modal-xl { max-width: 1200px; }
.modal-xxl { max-width: 1420px; }

.modal-header {
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
  height: 60px;
  background: #fff;
  color: #000;
  display: flex;
  align-items: center;
  width: 100%;
  box-shadow: 0px 6px 10px #0000000A;
  cursor: move;
  overflow: hidden;
}

.modal-header h2,
.modal-header :slotted(h2) {
  font-size: 18px;
  font-weight: 500;
  flex: 1;
  margin: 0;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  padding-left: 20px;
}

.close-div {
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 0 20px;
  flex: 0 0 auto;
  margin-left: auto;
  white-space: nowrap;
  cursor: pointer;
}

.close-label-div {
  font-size: 16px;
  font-weight: 500;
  color: #555;
}

.close-btn {
  margin-left: 10px;
}

.modal-body {
  padding: 30px;
  font-size: 14px;
  color: #000;
  max-height: calc(100vh - 180px);
  overflow-y: auto;
}

.modal-body :slotted(img) {
  max-width: 100%;
}

.modal-footer {
  padding: 1rem 1.5rem;
  border-top: 1px solid #e9ecef;
  text-align: right;
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
}

@media (max-width: 768px) {
  .modal-overlay {
    background-color: grey;
  }

  .modal-container {
    margin: 0;
    width: 100vw;
    height: 100vh;
    max-height: 100vh;
    border: 4px solid grey;
    border-radius: 10px;
    box-sizing: border-box;
  }

  .modal-xs,
  .modal-sm {
    max-width: 768px;
  }

  .modal-body {
    height: calc(100vh - 140px);
    overflow-y: auto;
  }
}
</style>
